import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { CollapsibleSection } from '../../components/CollapsibleSection'
import { TimelinePanel } from '../../components/timeline/TimelinePanel'
import { getBranch } from '../../api/branches'
import { ApiError } from '../../api/problem'
import type { BranchView } from '../../types/branch'
import { BranchFormModal } from './BranchFormModal'

/**
 * The first-ever Branch detail page (CRM completeness — IB-93). Reached from the Branches list
 * row-click/`Open` action. Header (name, Edit) -> `CollapsibleSection` "Details" (linked Legal
 * entity, country, region, permanent-establishment flag) -> `TimelinePanel` for the Branch's own
 * event stream. `Branch` carries no further child entity (data-model §3.1), so there is
 * deliberately no `RelatedList` here — unlike Account/LegalEntity/Contact.
 */
export function BranchDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [branch, setBranch] = useState<BranchView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getBranch(id)
      .then(({ branch: b }) => {
        if (!cancelled) setBranch(b)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load this branch.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <Link to="/clients/branches" style={{ color: 'var(--iw-text-muted)' }}>
          Branches
        </Link>{' '}
        › {branch?.name ?? id}
      </div>

      {error ? (
        <InlineAlert severity="error" action={<button type="button" className="iw-btn iw-btn--sm" onClick={() => navigate(0)}>Try again</button>}>
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading branch…</p>
      ) : branch ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--iw-accent)' }}>
              <Icon name="map-pin" size={22} />
            </span>
            <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>{branch.name}</h1>
            {branch.isPermanentEstablishment ? (
              <StatusPill variant="info" label="Permanent establishment" />
            ) : null}
            <span style={{ flex: 1 }} />
            <button type="button" className="iw-btn" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>

          <CollapsibleSection title="Details">
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 'var(--iw-space-4)',
                margin: 0,
              }}
            >
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Legal entity</dt>
                <dd style={{ margin: 0 }}>
                  <Link to={`/clients/legal-entities/${branch.legalEntityId}`} style={{ color: 'var(--iw-accent)' }}>
                    {branch.legalEntityName ?? branch.legalEntityId}
                  </Link>
                </dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Country</dt>
                <dd style={{ margin: 0 }}>{branch.country}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Region</dt>
                <dd style={{ margin: 0 }}>{branch.region}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Permanent establishment</dt>
                <dd style={{ margin: 0 }}>
                  {branch.isPermanentEstablishment ? (
                    <StatusPill variant="info" label="Yes" />
                  ) : (
                    <StatusPill variant="neutral" label="No" />
                  )}
                </dd>
              </div>
            </dl>
          </CollapsibleSection>

          <section>
            <h2 style={{ fontSize: 'var(--iw-size-md)', margin: '0 0 var(--iw-space-3)' }}>Timeline</h2>
            <TimelinePanel target={{ type: 'Branch', id: branch.id }} />
          </section>
        </>
      ) : null}

      {editing && branch ? (
        <BranchFormModal
          mode="edit"
          editing={{ branch, version: branch.version }}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setBranch(updated)
            setEditing(false)
          }}
        />
      ) : null}
    </div>
  )
}
